import React, { useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../config';
import type { ServiceNowLegacyAttachmentItem } from '../types';

const formatFileSize = (bytes?: number | null): string => {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const isPreviewableLegacyAttachment = (item: ServiceNowLegacyAttachmentItem): boolean => {
  const type = (item.content_type || '').toLowerCase();
  const name = (item.file_name || '').toLowerCase();
  return (
    type.startsWith('image/')
    || type === 'application/pdf'
    || name.endsWith('.pdf')
    || name.endsWith('.png')
    || name.endsWith('.jpg')
    || name.endsWith('.jpeg')
    || name.endsWith('.gif')
    || name.endsWith('.webp')
    || name.endsWith('.eml')
  );
};

async function parseAxiosBlobError(err: unknown): Promise<string | null> {
  if (!axios.isAxiosError(err) || !(err.response?.data instanceof Blob)) {
    return axios.isAxiosError(err)
      ? (err.response?.data as { detail?: string })?.detail || null
      : null;
  }
  try {
    const text = await err.response.data.text();
    const json = JSON.parse(text) as { detail?: string };
    return json.detail || null;
  } catch {
    return null;
  }
}

type LegacyAttachmentListProps = {
  items: ServiceNowLegacyAttachmentItem[];
  authHeader: Record<string, string>;
  title?: string;
  compact?: boolean;
  onError?: (message: string) => void;
};

const LegacyAttachmentList: React.FC<LegacyAttachmentListProps> = ({
  items,
  authHeader,
  title = 'Anexos disponíveis',
  compact = false,
  onError,
}) => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  if (!items.length) {
    return null;
  }

  const downloadAttachment = async (item: ServiceNowLegacyAttachmentItem, inline = false) => {
    if (downloadingId) return;
    setDownloadingId(item.sys_id);
    try {
      const response = await axios.get(
        `${API_URL}/api/servicenow-legacy/attachments/${item.sys_id}/download`,
        {
          headers: authHeader,
          params: { inline },
          responseType: 'blob',
        },
      );
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || item.content_type || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      if (inline) {
        window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }
      const link = document.createElement('a');
      link.href = url;
      link.download = item.file_name || 'anexo';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const detail = await parseAxiosBlobError(err);
      onError?.(detail || `Falha ao baixar ${item.file_name}.`);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className={`sn-legacy-attachment-panel${compact ? ' sn-legacy-attachment-panel-compact' : ''}`}>
      <div className="sn-legacy-attachment-panel-title">
        {title} ({items.length})
      </div>
      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="sn-legacy-attachments-table mb-0">
          <thead>
            <tr>
              {!compact && <th>Registro</th>}
              <th>Arquivo</th>
              {!compact && <th>Tipo</th>}
              <th>Tamanho</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.sys_id}>
                {!compact && <td>{item.record_number || '—'}</td>}
                <td>{item.file_name}</td>
                {!compact && <td>{item.content_type || '—'}</td>}
                <td>{formatFileSize(item.size_bytes)}</td>
                <td className="sn-legacy-attachment-actions">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={downloadingId === item.sys_id}
                    onClick={() => void downloadAttachment(item, false)}
                  >
                    Baixar
                  </Button>
                  {isPreviewableLegacyAttachment(item) && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="ms-2"
                      disabled={downloadingId === item.sys_id}
                      onClick={() => void downloadAttachment(item, true)}
                    >
                      Abrir
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default LegacyAttachmentList;
